import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, withModifiers as _withModifiers, createElementVNode as _createElementVNode, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createBlock as _createBlock, Fragment as _Fragment } from "vue"

const _hoisted_1 = ["onClick"]
const _hoisted_2 = {
  key: 0,
  class: "local-nginx-proxy-warning",
  role: "alert",
  lang: "en"
}
const _hoisted_3 = {
  key: 1,
  class: "from-portal-dev-frame",
  "aria-hidden": "true"
}

import {
  computed,
  onBeforeMount,
  onMounted,
  onUnmounted,
  onUpdated,
  ref,
  watch,
} from "vue";
import {
  brandingCodeFromRouteQuery,
  normalizeBrandingCode,
  syncBrandingCodeBodyClass,
} from "@/utils/brandingCodeBodyClass";
import { useRootStore } from "@/store/root";
import { useBookingStore } from "@/store/bookingModule";
import BookingTransitionBackdrop from "@/components/BookingForm/BookingTransitionBackdrop.vue";
import LiveChatLauncher from "@/components/LiveChatLauncher/LiveChatLauncher.vue";
import A11yLiveRegion from "@/components/A11yLiveRegion/A11yLiveRegion.vue";
import AccessibilitySidebarActions from "@/components/AccessibilityOptions/AccessibilitySidebarActions.vue";
import { useRoute, useRouter } from "vue-router";
import headLinkData from "./constants/headLinkData";
import utils from "./utils/utils";
import {
  disconnectVueformMultiselectA11y,
  observeVueformMultiselectA11y,
} from "@/utils/vueformMultiselectA11y";
import {
  FROM_PORTAL_QUERY,
  FROM_PORTAL_QUERY_ALIAS,
  isFromPortal,
  isBrokerRole,
} from "@/composables/fromPortal";
import { useI18n } from "vue-i18n";
import {
  htmlLangForAppLocale,
  localeFromRouteQuery,
  persistAppLocale,
  readStoredAppLocale,
  type AppLocale,
} from "@/composables/appLocale";
import { resolveDocumentsZipDownloadEnabled } from "@/utils/documentsZipDownloadConfig";
import {
  isConfigFlagEnabled,
  normalizeConfigFlagString,
} from "@/utils/configFlag";
import { applyTariffManagerLegalUiVisibility } from "@/utils/tariffManagerSearch";
import { disableFreshchatForTariffManagerTheme } from "@/utils/freshchat";
import {
  registerParentBrokerPostMessageListener,
  unregisterParentBrokerPostMessageListener,
} from "@/composables/embed/parentBrokerPostMessage";
import {
  registerDisplayPreferencesLocaleApplier,
  registerParentDisplayPreferencesPostMessageListener,
  unregisterDisplayPreferencesLocaleApplier,
  unregisterParentDisplayPreferencesPostMessageListener,
} from "@/composables/embed/parentDisplayPreferencesPostMessage";
import { schedulePostBrokerAffiliateStateToParent } from "@/composables/embed/postBrokerAffiliateStateToParent";
import {
  schedulePostDisplayPreferencesStateToParent,
  setDisplayPreferencesStateLocale,
} from "@/composables/embed/postDisplayPreferencesStateToParent";
import { parseEmbedDisplaySettingsFromRouteQuery } from "@/utils/embedDisplaySettings";
import {
  initA11yDisplayPreferences,
  registerA11yPreferencesChangeListener,
  setA11yDisplayPreferences,
  unregisterA11yPreferencesChangeListener,
} from "@/composables/useA11yDisplayPreferences";
import { focusMainContent } from "@/utils/a11yLiveRegion";
import { applyRoutePageTitle } from "@/utils/pageTitle";
import { waitForAppsBootstrap } from "@/utils/waitForAppsBootstrap";


export default /*#__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props) {

const route = useRoute();
const router = useRouter();
const rootStore = useRootStore();
const bookingStore = useBookingStore();
const { locale, t } = useI18n({ useScope: "global" });

const onSkipToContent = () => {
  focusMainContent();
};

/** Captured before Vue touches <head>: true when nginx/lua (modify_html.lua → /api/v1/meta_tags) already injected brand icon links. They have no id, so match by rel. If present, don't overwrite — Firefox picks the last <link rel="icon">, so an appended /covomo.png would win. */
const serverInjectedFavicon =
  typeof document !== "undefined" &&
  !!document.querySelector(
    'link[rel~="icon" i], link[rel~="apple-touch-icon" i]',
  );

/**
 * Same dimmed backdrop + spinner as booking wizard. Stays up from results
 * (“Jetzt beantragen”) through early Booking navigation until the first price
 * clears `redirectBooking` — so the loader accompanies the user onto the form
 * while the product tile + skeletons paint underneath.
 */
const bookingTransitionBackdropVisible = computed(() => {
  const busy =
    bookingStore.wizardNavigationOverlay || rootStore.redirectBooking;
  if (!busy) return false;
  if (route.name === "Booking") {
    /* Wizard overlay stays on through step 3 → completed (page 4) until footer clears it */
    if (bookingStore.wizardNavigationOverlay) return true;
    return bookingStore.currentBookingPage < 4;
  }
  return true;
});

const bookingTransitionBackdropVariant = computed<
  "belowBookingFooter" | "coverSearchChrome"
>(() =>
  route.name === "Booking" ? "belowBookingFooter" : "coverSearchChrome",
);

const isLocalhost = computed(() => {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return h === "localhost" || h === "127.0.0.1" || h === "[::1]";
});

/** Red inset frame on localhost or Covomo test hosts (e.g. rechner.test.covomo.de). */
const showNonProdDevFrame = computed(() => {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname.toLowerCase();
  if (isLocalhost.value) return true;
  return h.endsWith(".test.covomo.de");
});

const isDev = process.env.NODE_ENV === "development";

const localNginxProxyUnreachable = ref(false);

const checkLocalNginxProxy = () => {
  if (!isDev || !isLocalhost.value) return;
  const base = process.env.VUE_APP_TARIFF_MANAGER_URL?.replace(/\/$/, "");
  if (!base || !/^https?:\/\/(localhost|127\.0\.0\.1):8080$/.test(base)) {
    return;
  }
  const url = `${base}/api/v1/insurers?application=travel`;
  const controller = new AbortController();
  const t = window.setTimeout(() => controller.abort(), 5000);
  fetch(url, { signal: controller.signal })
    .then((r) => {
      if (!r.ok) localNginxProxyUnreachable.value = true;
    })
    .catch(() => {
      localNginxProxyUnreachable.value = true;
    })
    .finally(() => clearTimeout(t));
};

/** Prefer `?code=` from the URL when present; else store (e.g. resume booking). */
const effectiveBrandingCode = computed(() => {
  const fromUrl = brandingCodeFromRouteQuery(route.query);
  if (fromUrl) return fromUrl;
  return normalizeBrandingCode(rootStore.code);
});
const applyConfigValue = (
  config: Record<string, unknown>,

  key: string,

  applyValue: (value: unknown) => void,
) => {
  const value = config[key];

  if (value !== undefined && value !== null && value !== "") {
    applyValue(value);
  }
};

const applyCustomConfig = (config: Record<string, unknown>) => {
  if (!config) return;
  applyConfigValue(config, "show_legal_links", (value) => {
    const normalized = normalizeConfigFlagString(value, true);
    const showLegalLinks = isConfigFlagEnabled(value, true) ? "block" : "none";

    rootStore.setShowLegalLinks(normalized);

    document.body.style.setProperty("--show-legal-links", showLegalLinks);
  });

  applyConfigValue(config, "data_protection", (value) => {
    rootStore.dataProtectionLink = value;
  });

  applyConfigValue(config, "first_information", (value) => {
    rootStore.erstinformationLink = value;
  });

  applyConfigValue(config, "general_terms", (value) => {
    rootStore.agbLink = value;
  });

  applyConfigValue(config, "imprint_link", (value) => {
    rootStore.setImpressumLink(value);
  });

  applyConfigValue(config, "contract_cancellation_link", (value) => {
    rootStore.setCancelContractLink(value);
  });

  applyConfigValue(config, "contact_link", (value) => {
    rootStore.setContactLink(value);
  });

  applyConfigValue(config, "enable_sharing", (value) => {
    rootStore.setEnableSharing(normalizeConfigFlagString(value, true));
  });

  applyConfigValue(config, "enable_saving", (value) => {
    rootStore.setEnableSaving(normalizeConfigFlagString(value, true));
  });

  applyConfigValue(config, "enable_sharing_with_broker", (value) => {
    rootStore.setEnableSharingWithBroker(
      normalizeConfigFlagString(value, true),
    );
  });

  applyConfigValue(config, "enable_documents_zip_download", (value) => {
    rootStore.setEnableDocumentsZipDownload(
      resolveDocumentsZipDownloadEnabled(value),
    );
  });

  applyConfigValue(config, "enable_comparison", (value) => {
    rootStore.setEnableComparison(normalizeConfigFlagString(value, true));
  });

  applyConfigValue(config, "enable_basisberatung_disclaimer", (value) => {
    rootStore.setEnableBasisberatung(normalizeConfigFlagString(value, true));
  });
  applyTariffManagerLegalUiVisibility(
    rootStore.theme ?? route.query.theme,
    rootStore,
  );
  disableFreshchatForTariffManagerTheme(rootStore.theme ?? route.query.theme);
};

const samplePrevPrev = ref<number | null>(null);
const samplePrev = ref<number | null>(null);
const sampleCurrent = ref<number | null>(null);
let sizeCheckInterval: number | null = null;

const checkAppSize = () => {
  const appEl = document.getElementById("app");
  const appHeight = appEl?.offsetHeight ?? 0;
  sampleCurrent.value = appHeight;

  if (samplePrevPrev.value === null && samplePrev.value === null) {
    samplePrevPrev.value = appHeight;
    samplePrev.value = appHeight;
    return;
  }

  if (
    sampleCurrent.value === samplePrev.value &&
    samplePrev.value !== samplePrevPrev.value
  ) {
    utils.iFrameResize("*", undefined);
  }

  samplePrevPrev.value = samplePrev.value;
  samplePrev.value = sampleCurrent.value;
};

onUpdated(() => {
  const rawTheme = route.query.theme;
  const themeKey =
    rawTheme && headLinkData[`${rawTheme}`] ? rawTheme : "covomo";
  if (!rawTheme) {
    rootStore.setTheme("covomo");
  } else if (!headLinkData[`${rawTheme}`]) {
    rootStore.setTheme("covomo");

    router.replace({
      query: {
        ...route.query,
        theme: "covomo",
      },
    });
  }

  if (!serverInjectedFavicon) {
    const linkData = headLinkData[`${themeKey}`];
    utils.createMetaDataLink(linkData);
  }
});

onBeforeMount(() => {
  applyCustomConfig(window?.rechnerCustomConfig);
  initA11yDisplayPreferences();
});

void waitForAppsBootstrap().then(() => {
  applyCustomConfig(window?.rechnerCustomConfig);
  applyRoutePageTitle(route, t, bookingStore.currentBookingPage);
});

watch(
  effectiveBrandingCode,
  (code) => {
    syncBrandingCodeBodyClass(code);
  },
  { immediate: true },
);

/** Parent embed: push broker + affiliate (`a`/`r`) snapshot after changes (debounced). */
watch(
  () => ({
    brokerPhone: rootStore.brokerPhone,
    brokerName: rootStore.brokerName,
    brokerCompany: rootStore.brokerCompany,
    brokerEmail: rootStore.brokerEmail,
    poolEmail: rootStore.poolEmail,
    poolName: rootStore.poolName,
    brokerId: rootStore.brokerId,
    brokerSubId: rootStore.brokerSubId,
    brokerInfoPrefill: rootStore.brokerInfoPrefill,
    brokerInfovisible: rootStore.brokerInfovisible,
    hasAParameter: rootStore.hasAParameter,
    hasRParameter: rootStore.hasRParameter,
    affiliateData: rootStore.affiliateData,
    qA: route.query.a,
    qR: route.query.r,
  }),
  () => {
    schedulePostBrokerAffiliateStateToParent();
  },
  { deep: true, flush: "post", immediate: true },
);

watch(
  () => [route.fullPath, bookingStore.currentBookingPage] as const,
  () => {
    applyRoutePageTitle(route, t, bookingStore.currentBookingPage);
  },
  { immediate: true },
);

const applyAppLocale = (resolved: AppLocale) => {
  locale.value = resolved;
  persistAppLocale(resolved);
  rootStore.setLanguage(resolved);
  setDisplayPreferencesStateLocale(resolved);
  if (typeof document !== "undefined") {
    document.documentElement.setAttribute(
      "lang",
      htmlLangForAppLocale(resolved),
    );
  }
};

watch(
  () => route.query,
  () => {
    const q = route.query;
    const portal = isFromPortal(q);
    rootStore.setFromPortal(portal);

    if (q.origin === "tariff-manager") {
      rootStore.setOrigin("tariff-manager");
    }

    const embedSettings = parseEmbedDisplaySettingsFromRouteQuery(q);
    if (Object.keys(embedSettings.displayPrefs).length > 0) {
      setA11yDisplayPreferences(embedSettings.displayPrefs);
    }

    const resolved = embedSettings.locale ?? localeFromRouteQuery(q);
    if (resolved) {
      applyAppLocale(resolved);
    } else {
      const stored = readStoredAppLocale();
      if (stored) {
        applyAppLocale(stored);
      } else {
        applyAppLocale("de");
      }
    }
    if (
      isBrokerRole(q[FROM_PORTAL_QUERY]) ||
      isBrokerRole(q[FROM_PORTAL_QUERY_ALIAS])
    ) {
      const nextQuery = { ...q };
      delete nextQuery[FROM_PORTAL_QUERY];
      delete nextQuery[FROM_PORTAL_QUERY_ALIAS];
      router.replace({ query: nextQuery });
    }
    schedulePostDisplayPreferencesStateToParent();
  },
  { deep: true, immediate: true },
);

onMounted(() => {
  checkLocalNginxProxy();
  sizeCheckInterval = window.setInterval(checkAppSize, 500);
  registerParentBrokerPostMessageListener();
  registerDisplayPreferencesLocaleApplier(applyAppLocale);
  registerParentDisplayPreferencesPostMessageListener();
  registerA11yPreferencesChangeListener(
    schedulePostDisplayPreferencesStateToParent,
  );
  observeVueformMultiselectA11y(document);
});

onUnmounted(() => {
  if (sizeCheckInterval) clearInterval(sizeCheckInterval);
  unregisterParentBrokerPostMessageListener();
  unregisterDisplayPreferencesLocaleApplier();
  unregisterParentDisplayPreferencesPostMessageListener();
  unregisterA11yPreferencesChangeListener();
  disconnectVueformMultiselectA11y(document);
});

return (_ctx: any,_cache: any) => {
  const _component_router_view = _resolveComponent("router-view")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("a", {
      href: "#main-content",
      class: "skip-to-content",
      onClick: _withModifiers(onSkipToContent, ["prevent"])
    }, _toDisplayString(_unref(t)("message.SKIP_TO_CONTENT")), 9, _hoisted_1),
    _createVNode(A11yLiveRegion),
    (localNginxProxyUnreachable.value && _unref(isLocalhost) && isDev)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, " Local nginx is not responding on port 8080 (API proxy). Start nginx, then reload. This app runs on port 8081 in development. "))
      : _createCommentVNode("", true),
    (_unref(showNonProdDevFrame))
      ? (_openBlock(), _createElementBlock("div", _hoisted_3))
      : _createCommentVNode("", true),
    _createVNode(BookingTransitionBackdrop, {
      visible: _unref(bookingTransitionBackdropVisible),
      variant: _unref(bookingTransitionBackdropVariant)
    }, null, 8, ["visible", "variant"]),
    _createVNode(LiveChatLauncher),
    (_openBlock(), _createBlock(_component_router_view, {
      key: _ctx.$route.fullPath
    })),
    _createVNode(AccessibilitySidebarActions)
  ], 64))
}
}

})